import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { Cell, Row, downloadSheets, generateSheet } from '../../../utils';
import {
  Dropdown,
  DropdownButton,
  Row as BSRow,
  Table,
} from 'react-bootstrap';
import { AuthedLayout } from '../../../components';
import { DeskMaxtrix, UserInfo, userInfoZod } from '../../../model';
import { useSemGrade } from '../../../hook';
import apis from '../../../apis';
import { toast } from 'react-toastify';

const borderStyle = { color: { rgb: '000000' }, style: 'thin' } as const;
const fullBorderStyle = {
  top: borderStyle,
  bottom: borderStyle,
  left: borderStyle,
  right: borderStyle,
} as const;
const cellAlignStyle = {
  horizontal: 'center',
  vertical: 'center',
} as const;

const mapState = (state: ApplicationState) => ({ ...state.auth });
const mapDispatches = ErrorDispatches;

const connector = connect(mapState, mapDispatches);

type Props = ConnectedProps<typeof connector>;

const allDeskIds = [0, 110,
  115,
  120,
  125,
  130,
  135,
  140,
  145,
  150,
  155,
  160,
  165,
  170,
  175,
  180];

const whDeskMaxtrix: FunctionComponent<Props> = ({ catchErrorForModal }) => {
  const { yearSem, element } = useSemGrade();
  const [datas, setDatas] = useState<DeskMaxtrix[]>([]);
  const [userinfo, setUser] = useState<UserInfo>();
  useEffect(() => {
    if (yearSem) {
      apis.getUserInfo()
        .then((u) => {
          setUser(u);
        })
        .catch(catchErrorForModal);
      toast
        .promise(
          apis
            .geWHDeskMaxtrix(yearSem.year, yearSem.sem)
            .then(r => {
              setDatas(r);
            }),
          {
            pending: '資料載入中......',
            success: '資料載入成功！',
            error: '資料載入失敗！請查看錯誤資訊。',
          }
        )
        .catch(catchErrorForModal);

    }
  }, [yearSem?.year, yearSem?.sem]);
  console.log(datas)

  const groupedByGrade: Record<number, any[]> = {};

  datas.forEach(d => {
    const deskId = Number(d.deskID);
    const grade = d.grade;
    const no = d.className;

    groupedByGrade[grade] = groupedByGrade[grade] || [];

    let classEntry = groupedByGrade[grade].find(item => item.no === no);

    if (!classEntry) {
      classEntry = { grade, no };
      allDeskIds.forEach(did => classEntry[did] = 0);
      groupedByGrade[grade].push(classEntry);
    }

    classEntry[deskId] = d.count;
  });


  const contentRows = generateDeskRowsWithGradeAndTotalSummary(groupedByGrade, allDeskIds);
  const deskIDrows = allDeskIds.map(deskId => ({
    value: deskId,
    merge: { column: 1 },
    style: { border: fullBorderStyle },
  }));
  const content: Row[] = [
    {
      cells: [
        {
          value: '年班＼型號',
          merge: { column: 2 },
          style: { border: fullBorderStyle },
        },
        ...deskIDrows,
        {
          value: '小計',
          merge: { column: 1 },
          style: { border: fullBorderStyle },
        },
      ],
    },
    ...contentRows,
  ];

  const workSheet = generateSheet(content, { alignment: cellAlignStyle });
  const handleExcelDownload = () => {
    const workSheet = generateSheet(
      [
        {
          cells: [
            {
              value: `${yearSem?.year}學年第${yearSem?.sem}學期_課桌椅型號統計`,
              style: {
                alignment: {
                  horizontal: 'left',
                },
              },
            },
          ],
        },
        ...content,
        {
          cells: [
            {
              value:
                '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
              style: {
                alignment: {
                  horizontal: 'left',
                },
              },
            },
          ],
        },
      ],
      {
        alignment: cellAlignStyle,
      }
    );

    downloadSheets(
      `${yearSem?.year}學年第${yearSem?.sem}學期_課桌椅型號統計`,
      userinfo?.exportFileType,
      {
        sheet: workSheet,
        name: 'st',
      }
    );
  };

  const handlePrint = () => {
    const printWindow = window.open(
      '',
      '_blank',
      'width=1400,height=900'
    );

    if (!printWindow) {
      alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
      return;
    }

    const escapeHtml = (value: unknown): string => {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    const title =
      `${yearSem?.year}學年第${yearSem?.sem}學期_課桌椅型號統計`;

    const tableRows = content
      .map((row) => {
        const cells = row.cells
          .map((cell) => {
            if (cell.value === undefined) {
              return '';
            }

            const colSpan = cell.merge?.column
              ? ` colspan="${cell.merge.column}"`
              : '';

            const rowSpan = cell.merge?.row
              ? ` rowspan="${cell.merge.row}"`
              : '';

            return `
            <td${colSpan}${rowSpan}>
              ${escapeHtml(cell.value)}
            </td>
          `;
          })
          .join('');

        return `<tr>${cells}</tr>`;
      })
      .join('');

    printWindow.document.open();

    printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="zh-Hant">
      <head>
        <meta charset="UTF-8" />
        <title>${escapeHtml(title)}</title>

        <style>
          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            font-family:
              "Microsoft JhengHei",
              "Noto Sans TC",
              Arial,
              sans-serif;
            color: #000;
          }

          .print-page {
            min-height: 190mm;
            display: flex;
            flex-direction: column;
          }

          h1 {
            margin: 0 0 10px;
            text-align: center;
            font-size: 18px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }

          td {
            border: 1px solid #000;
            padding: 4px 2px;
            text-align: center;
            vertical-align: middle;
            font-size: 8px;
            word-break: break-word;
          }

          .footer {
  margin-top: auto;
  padding-top: 16px;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  width: 100%;
}

          @page {
            size: A4 landscape;
            margin: 6mm;
          }

          @media print {
            body {
              padding: 0;
            }

            tr {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          }
        </style>
      </head>

      <body>
        <div class="print-page">

          <h1>${escapeHtml(title)}</h1>

          <table>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div class="footer">
  <span>承辦人:</span>
  <span>組長:</span>
  <span>主任:</span>
  <span>校長:</span>
</div>

        </div>

        <script>
          window.onload = function () {
            window.focus();

            setTimeout(function () {
              window.print();
            }, 300);
          };

          window.onafterprint = function () {
            window.close();
          };
        </script>
      </body>
    </html>
  `);

    printWindow.document.close();
  };
  return (
    <AuthedLayout>
      <BSRow className="justify-content-between">
        <span className="mr-3">{element}</span>
        <DropdownButton
          id="wh-desk-matrix-export"
          title="匯出"
          variant="success"
          disabled={!datas.length}
        >
          <Dropdown.Item onClick={handleExcelDownload}>
            Excel 下載
          </Dropdown.Item>

          <Dropdown.Item onClick={handlePrint}>
            列印
          </Dropdown.Item>
        </DropdownButton>
      </BSRow>
      <hr />
      <BSRow>
        <Table
          striped
          bordered
          className="text-center"
          style={{ tableLayout: 'fixed' }}
        >
          <tbody>
            {content.map((r, i) => {
              const cells = r.cells.map((c, j) =>
                c.value != undefined ? (
                  <td
                    colSpan={c.merge?.column}
                    rowSpan={c.merge?.row}
                    key={`${i}-${j}`}
                  >
                    {c.value}
                  </td>
                ) : (
                  <Fragment key={`${i}-${j}`}></Fragment>
                )
              );
              return <tr key={i}>{cells}</tr>;
            })}
          </tbody>
        </Table>
      </BSRow>
    </AuthedLayout>
  );
};

function generateDeskRowsWithGradeAndTotalSummary(
  groupedByGrade: Record<number, any[]>,
  allDeskIds: number[]
): Row[] {

  const rows: Row[] = [];

  const allGrades = Object.keys(groupedByGrade[0] ?? []).map(Number);
  const maxGrade = Math.max(...allGrades);

  const schoolSummary: Record<number, number> = Object.fromEntries(
    allDeskIds.map((id) => [id, 0])
  );

  Object.entries(groupedByGrade).forEach(([gradeStr, classList]) => {

    const grade = Number(gradeStr);

    const gradeSummary: Record<number, number> = Object.fromEntries(
      allDeskIds.map((id) => [id, 0])
    );

    classList.forEach((cls, index) => {
      const gradeMerge = index === 0 ? { row: classList.length, column: 1 } : undefined;

      const gradeCell = {
        value: gradeMerge ? grade : undefined,
        merge: gradeMerge,
        style: { border: fullBorderStyle },
      };

      const classCell = {
        value: cls.no,
        merge: { row: 1 },
        style: { border: fullBorderStyle },
      };

      const deskCells = allDeskIds.map((deskId) => {
        const count = cls[deskId] ?? 0;
        gradeSummary[deskId] += count;
        schoolSummary[deskId] += count;
        return {
          value: count,
          style: { border: fullBorderStyle },
        };
      });

      const totalCount = allDeskIds.reduce((sum, did) => sum + (cls[did] || 0), 0);
      if (grade !== maxGrade) {
        rows.push({
          cells: [
            gradeCell,
            classCell,
            ...deskCells,
            {
              value: totalCount,
              style: { border: { right: borderStyle } },
            },
          ],
        });
      }
    });
    if (grade !== maxGrade) {
      const gradeTotal = Object.values(gradeSummary).reduce((a, b) => a + b, 0);
      rows.push({
        cells: [
          {
            value: `${grade} 年級小計`,
            merge: { column: 2 },
            style: { border: fullBorderStyle },
          },
          ...allDeskIds.map((deskId) => ({
            value: gradeSummary[deskId],
            style: { border: fullBorderStyle },
          })),
          {
            value: gradeTotal,
            style: { border: { right: borderStyle } },
          },
        ],
      });
    }
  });



  return rows;
}



export const WHDeskMaxtrix = connector(whDeskMaxtrix);
